import { useEffect, useMemo, useRef, useState } from "react";

type UseBlobToImageOptions = {
  onCleanup?: () => void; // Optional cleanup callback
};

/**
 * Turn a Blob into an object URL for the lifetime of that blob.
 *
 * The URL is derived SYNCHRONOUSLY (`useMemo`) rather than published through
 * state, and the previous URL is revoked from an effect keyed on the new one —
 * i.e. only AFTER the replacement has been committed to the DOM. Both details
 * matter:
 *
 *  - Creating it in state cost an extra render, so the first paint after a blob
 *    arrived still had no `src`.
 *  - Revoking in the previous effect's cleanup released the old URL while the
 *    DOM still pointed at it, so the browser painted one frame against a
 *    revoked blob: URL and drew its broken-image glyph (a blue "?" in WebKit).
 *    Swiping between attachments flashed exactly that.
 *
 * `onCleanup` is read through a ref so an inline `{ onCleanup }` object can
 * never re-trigger create/revoke churn.
 */
const useBlobToImageURL = (
  blob: Blob | null,
  options?: UseBlobToImageOptions,
): string => {
  // Bumped when React 18 StrictMode's simulated unmount (dev only) revokes a
  // URL that was minted during the MOUNT render: the double-mount re-runs
  // effects but NOT the memo, so without this the remount kept returning the
  // revoked URL and every already-cached image rendered broken (mass "blur"
  // on any chat switch in dev). Production mounts never revoke-then-remount,
  // so the bump never fires there.
  const [mintKey, setMintKey] = useState(0);
  const url = useMemo(() => {
    if (!blob || blob.size === 0) {
      return "";
    }
    try {
      return URL.createObjectURL(blob);
    } catch {
      return "";
    }
    // mintKey forces a fresh URL after a StrictMode revoke (see above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blob, mintKey]);

  const onCleanupRef = useRef(options?.onCleanup);
  onCleanupRef.current = options?.onCleanup;

  // The url whose revoke has already run — if an effect SETUP sees its own
  // current url in here, this is the second half of a StrictMode double-mount
  // and the on-screen URL is dead: mint a replacement.
  const revokedRef = useRef<string>("");

  useEffect(() => {
    if (!url) {
      return;
    }
    if (revokedRef.current === url) {
      revokedRef.current = "";
      setMintKey((k) => k + 1);
      return;
    }
    // Runs when `url` changes (its successor is already rendered) or on
    // unmount — never while this URL is still the one on screen.
    return () => {
      URL.revokeObjectURL(url);
      revokedRef.current = url;
      onCleanupRef.current?.();
    };
  }, [url]);

  return url;
};

export default useBlobToImageURL;
